import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const ProjectsSection = () => {
  const navigate = useNavigate();
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if IntersectionObserver is supported
    if (!window.IntersectionObserver) {
      // Fallback: show all cards immediately if IntersectionObserver is not supported
      const allIndices = Array.from({ length: 6 }, (_, i) => i);
      setVisibleCards(allIndices);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('[data-project-card]');
            cards.forEach((card, index) => {
              setTimeout(() => {
                setVisibleCards(prev => [...new Set([...prev, index])]);
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleViewAllProjects = () => {
    try {
      navigate('/galerie');
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback: use window.location if react-router fails
      window.location.href = '/galerie';
    }
  };

  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index));
  };

  const projects = [
    {
      title: 'Villa Moderne',
      category: 'Construction Neuve',
      image: 'https://static.readdy.ai/image/f4befc687b50546f577c3e945d9e0997/c6db8b5e4837d63ba80a1dd039bd0016.jpeg',
      description: 'Villa contemporaine de 250m² avec piscine et jardin paysager.',
      details: ['250m² habitables', 'Piscine chauffée', 'Domotique intégrée'],
      year: '2025'
    },
    {
      title: 'Rénovation Haussmannienne - Lubumbashi',
      category: 'Rénovation',
      image: 'https://static.readdy.ai/image/f4befc687b50546f577c3e945d9e0997/3af4639119a1668aac4886a0a7f8ad60.jpeg',
      description: 'Rénovation complète d\'un appartement de 180m².',
      details: ['180m² rénovés', 'Cachet préservé', 'Équipements modernes'],
      year: '2025'
    },
    {
      title: 'Extension Contemporaine - Kolwezi',
      category: 'Extension',
      image: 'https://static.readdy.ai/image/f4befc687b50546f577c3e945d9e0997/bc22bd179606bfd827871e603a239d47.jpeg',
      description: 'Extension moderne de 80m² avec verrière et terrasse.',
      details: ['80m² ajoutés', 'Verrière design', 'Terrasse panoramique'],
      year: '2025'
    },
    {
      title: 'Bureaux Modernes - Likasi',
      category: 'Commercial',
      image: 'https://static.readdy.ai/image/f4befc687b50546f577c3e945d9e0997/4c01aae2db88726abf279ce5d42f74ed.jpeg',
      description: 'Aménagement de bureaux modernes de 500m² pour startup.',
      details: ['500m² aménagés', 'Espaces collaboratifs', 'Design innovant'],
      year: '2025'
    },
    {
      title: 'Maison Écologique - Kipushi',
      category: 'Construction Neuve',
      image: 'https://static.readdy.ai/image/f4befc687b50546f577c3e945d9e0997/fc1b8cb7924ac8f1e7accb5f0752ae90.jpeg',
      description: 'Maison passive de 200m² avec panneaux solaires et récupération d\'eau.',
      details: ['Maison passive', 'Panneaux solaires', 'Récupération d\'eau'],
      year: '2025'
    },
    {
      title: 'Loft Industriel - Kasumbalesa',
      category: 'Rénovation',
      image: 'https://static.readdy.ai/image/f4befc687b50546f577c3e945d9e0997/9496f36e9066230763912f1927f66c8f.jpeg',
      description: 'Transformation d\'un ancien atelier en loft de 150m².',
      details: ['150m² transformés', 'Style industriel', 'Volumes exceptionnels'],
      year: '2025'
    }
  ];

  return (
    <section id="projets" className="py-12 sm:py-16 lg:py-20 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Nos <span className="text-blue-600">Réalisations</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            Découvrez quelques-unes de nos réalisations les plus remarquables. Chaque projet reflète notre engagement envers l'excellence et l'innovation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          {projects.map((project, index) => (
            <div 
              key={index} 
              data-project-card
              className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                visibleCards.includes(index) 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
              style={{
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
              }}
            >
              <div className="relative overflow-hidden group">
                {imageErrors.has(index) ? (
                  <div className="w-full h-48 sm:h-56 md:h-64 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">Image non disponible</span>
                  </div>
                ) : (
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-48 sm:h-56 md:h-64 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    onError={() => handleImageError(index)}
                    loading="lazy"
                  />
                )}
                <div className="absolute top-3 left-3 sm:top-4 sm:left-4 transform group-hover:scale-110 transition-transform duration-300">
                  <span className="bg-blue-600 text-white px-2.5 py-1 sm:px-3 rounded-full text-xs sm:text-sm font-medium shadow-lg">
                    {project.category}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">{project.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{project.description}</p>
                <ul className="space-y-1">
                  {project.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center text-xs sm:text-sm text-gray-700 transform hover:translate-x-1 transition-transform duration-200">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 flex-shrink-0">
                        <i className="ri-check-line text-blue-600"></i>
                      </div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-12">
          <button
            onClick={handleViewAllProjects}
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl text-sm sm:text-base"
            aria-label="Voir toutes nos réalisations"
          >
            Voir toutes nos réalisations
            <div className="w-5 h-5 flex items-center justify-center ml-2">
              <i className="ri-arrow-right-line"></i>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
